import React, { useState } from 'react';
import { Modal } from './ui/modal';

const DEFAULT_DIETARY_RESTRICTIONS = {
  dairy: false,
  eggs: false,
  gluten: false,
  nuts: false,
  peanuts: false,
  sesame: false,
  shellfish: false,
  soy: false,
  coconut: false,
  pork: false,
  // vegetarian: false,
  // vegan: false,
  lowSodium: false,
  lowCarb: false,
  lowCalorie: false
};

export function SettingsModal({ isOpen, onClose, userInfo, onUpdate }) {
  const [formData, setFormData] = useState(() => {
    const savedData = JSON.parse(localStorage.getItem('userData') || '{}');
    
    // Convert old treeNuts to new nuts in existing data
    const dietaryRestrictions = savedData.dietaryRestrictions || userInfo?.dietaryRestrictions || {
      ...DEFAULT_DIETARY_RESTRICTIONS
    };
    
    // If there's an old treeNuts property, migrate it to nuts
    if ('treeNuts' in dietaryRestrictions) {
      dietaryRestrictions.nuts = dietaryRestrictions.treeNuts;
      delete dietaryRestrictions.treeNuts;
    }

    return {
      homeId: savedData.homeId || userInfo?.homeId || '',
      contactEmail: savedData.contactEmail || '',
      phoneNumber: savedData.phoneNumber || userInfo?.phoneNumber || '',
      householdSize: savedData.householdSize || userInfo?.householdSize || 1,
      dietaryRestrictions,
      dietaryLimits: savedData.dietaryLimits || userInfo?.dietaryLimits || {
        sodium: '',
        carbs: '',
        calories: ''
      }
    };
  });

  const [errors, setErrors] = useState({
    phoneNumber: '',
    email: '',
    homeId: ''
  });

  const handleDietaryChange = (restriction) => {
    setFormData(prev => {
      const newState = {
        ...prev,
        dietaryRestrictions: {
          ...prev.dietaryRestrictions,
          [restriction]: !prev.dietaryRestrictions[restriction]
        }
      };

      // Set default values when checkboxes are checked
      if (!prev.dietaryRestrictions[restriction]) {
        switch (restriction) {
          case 'lowSodium':
            newState.dietaryLimits = { ...prev.dietaryLimits, sodium: '700' };
            break;
          case 'lowCalorie':
            newState.dietaryLimits = { ...prev.dietaryLimits, calories: '500' };
            break;
          case 'lowCarb':
            newState.dietaryLimits = { ...prev.dietaryLimits, carbs: '40' };
            break;
        }
      }

      return newState;
    });
  };

  const validateForm = () => {
    const newErrors = {};
    const phoneRegex = /^\d{10}$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    if (!phoneRegex.test(formData.phoneNumber.replace(/[^0-9]/g, ''))) {
      newErrors.phoneNumber = 'Please enter a valid 10-digit phone number';
    }
    
    if (!formData.contactEmail || !emailRegex.test(formData.contactEmail)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData.homeId.trim()) {
      newErrors.homeId = 'Home ID is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const userData = {
      ...formData,
      phoneNumber: formData.phoneNumber.replace(/[^0-9]/g, '')
    };

    localStorage.setItem('userData', JSON.stringify(userData));
    onUpdate(userData);
    onClose();
  };

  // Add this after the handleDietaryChange function
const handleDietaryLimitChange = (type, value) => {
  setFormData(prev => ({
    ...prev,
    dietaryLimits: {
      ...prev.dietaryLimits,
      [type]: value
    }
  }));
};

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="p-6 max-h-[90vh] overflow-y-auto w-[95vw] max-w-[1200px]"> {/* Modified this line */}
        <h2 className="text-2xl font-bold mb-4">Update Your Info</h2>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Dietary Restrictions Section */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-xl font-semibold mb-4">Dietary Restrictions</h3>
            <div className="space-y-2">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4"> {/* Modified this line */}
                {Object.entries(DEFAULT_DIETARY_RESTRICTIONS)
                  .filter(([key]) => !['lowSodium', 'lowCarb', 'lowCalorie'].includes(key))
                  .map(([restriction, _]) => (
                    <div key={restriction} className="flex items-center gap-2 p-2 bg-white rounded border border-gray-200">
                      <input
                        type="checkbox"
                        id={`restriction-${restriction}`}
                        checked={formData.dietaryRestrictions[restriction]}
                        onChange={() => handleDietaryChange(restriction)}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <label htmlFor={`restriction-${restriction}`} className="text-sm text-gray-900">
                        {restriction === 'nuts' ? 'Nuts' : 
                          restriction.charAt(0).toUpperCase() + restriction.slice(1)} {!['vegetarian', 'vegan'].includes(restriction) ? 'Free' : ''}
                      </label>
                    </div>
                  ))}
              </div>
            </div>
          </div>

          {/* Dietary Limits Section */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mt-6">
            <h3 className="text-xl font-semibold mb-4">Dietary Limits</h3>
            <div className="space-y-2">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {/* Low Sodium */}
                <div className="flex items-center gap-2 p-2 bg-white rounded border border-gray-200">
                  <input
                    type="checkbox"
                    id="lowSodium"
                    checked={formData.dietaryRestrictions.lowSodium}
                    onChange={() => handleDietaryChange('lowSodium')}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor="lowSodium" className="text-sm text-gray-900 flex-grow">
                    Low Sodium
                  </label>
                  {formData.dietaryRestrictions.lowSodium && (
                    <div className="relative w-24">
                      <input
                        type="number"
                        value={formData.dietaryLimits.sodium}
                        onChange={(e) => handleDietaryLimitChange('sodium', e.target.value)}
                        className="w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 pr-8 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        min="0"
                      />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-sm">mg</span>
                    </div>
                  )}
                </div>

                {/* Low Carb */}
                <div className="flex items-center gap-2 p-2 bg-white rounded border border-gray-200">
                  <input
                    type="checkbox"
                    id="lowCarb"
                    checked={formData.dietaryRestrictions.lowCarb}
                    onChange={() => handleDietaryChange('lowCarb')}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor="lowCarb" className="text-sm text-gray-900 flex-grow">
                    Low Carb
                  </label>
                  {formData.dietaryRestrictions.lowCarb && (
                    <div className="relative w-24">
                      <input
                        type="number"
                        value={formData.dietaryLimits.carbs}
                        onChange={(e) => handleDietaryLimitChange('carbs', e.target.value)}
                        className="w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 pr-8 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        min="0"
                      />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-sm">g</span>
                    </div>
                  )}
                </div>

                {/* Low Calorie */}
                <div className="flex items-center gap-2 p-2 bg-white rounded border border-gray-200">
                  <input
                    type="checkbox"
                    id="lowCalorie"
                    checked={formData.dietaryRestrictions.lowCalorie}
                    onChange={() => handleDietaryChange('lowCalorie')}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor="lowCalorie" className="text-sm text-gray-900 flex-grow">
                    Low Calorie
                  </label>
                  {formData.dietaryRestrictions.lowCalorie && (
                    <div className="relative w-24">
                      <input
                        type="number"
                        value={formData.dietaryLimits.calories}
                        onChange={(e) => handleDietaryLimitChange('calories', e.target.value)}
                        className="w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 pr-8 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        min="0"
                      />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-sm">cal</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Other Form Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6"> {/* Modified this line */}
            <div>
              <label htmlFor="homeId" className="block text-sm font-medium text-gray-700 mb-1">
                Home ID
              </label>
              <input
                type="text"
                id="homeId"
                value={formData.homeId}
                onChange={(e) => setFormData(prev => ({ ...prev, homeId: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter your Home ID"
              />
              {errors.homeId && <p className="text-red-500 text-sm mt-1">{errors.homeId}</p>}
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number
              </label>
              <input
                type="tel"
                id="phone"
                value={formData.phoneNumber}
                onChange={(e) => setFormData(prev => ({ ...prev, phoneNumber: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="(123) 456-7890"
              />
              {errors.phoneNumber && <p className="text-red-500 text-sm mt-1">{errors.phoneNumber}</p>}
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Contact Email
              </label>
              <input
                type="email"
                id="email"
                value={formData.contactEmail}
                onChange={(e) => setFormData(prev => ({ ...prev, contactEmail: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter your contact email"
              />
              {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
            </div>

            <div>
              <label htmlFor="household" className="block text-sm font-medium text-gray-700 mb-1">
                Household Size
              </label>
              <input
                type="number"
                id="household"
                min="1"
                max="20"
                value={formData.householdSize}
                onChange={(e) => setFormData(prev => ({ ...prev, householdSize: Number(e.target.value) }))}
                className="w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 pr-8 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
            </div>
          </div>

          <button
            type="submit"
            className="w-full bg-blue-600 text-white rounded-md py-3 hover:bg-blue-700 transition-colors font-medium"
          >
            Save Changes
          </button>
        </form>
      </div>
    </Modal>
  );
}